import { isObject } from 'lodash';
import { captureException } from '@sentry/react';
import { api } from 'domains/api';
import { BASE_ANONYMOUS_API_ROUTES, BASE_API_ROUTES, getBaseAPIRoute } from 'domains/api/base/routes';
import { StateAbbrev, toStateFull } from 'utils/states';
import { RawClaim, Payment, PaymentMethod, ClientBillingInfo, Claim, PaymentIntentsItems, PaymentItems } from './types';

const MAX_PAYMENT_PAGE_SIZE = 5;
const MAX_CLAIM_PAGE_SIZE = 5;

export type ClaimsResponse = {
  items: Claim[];
  currentPage: number | null;
  nextPage: number | null;
  previousPage: number | null;
};



export type PaymentResponse = {
  invoices: PaymentItems;
  payments: PaymentIntentsItems;
};

export const getClaims = (contactId: string, clientId: string, page?: number | null): Promise<ClaimsResponse> => {
  return api.base
    .get<ClaimsResponse>(
      getBaseAPIRoute(BASE_API_ROUTES.contacts[':contactId'].clients[':clientId'].claims.all, {
        clientId,
        contactId,
      }),
      {
        params: {
          perPage: MAX_CLAIM_PAGE_SIZE,
          page,
        },
      },
    )
    .then(response => response);
};

export const getBillingInformationByClientId = (
  contactId: string,
  clientId: string,
): Promise<ClientBillingInfo | undefined> => {
  return api.base
    .get<ClientBillingInfo | undefined>(
      getBaseAPIRoute(BASE_API_ROUTES.contacts[':contactId'].clients[':clientId'].billing.info, {
        clientId,
        contactId,
      }),
    )
    .then(billing => {
      if (billing) {
        return {
          ...billing,
          automaticCoInsuranceCollection: billing.automaticCoInsuranceCollection ?? false,
        };
      }
      return undefined;
    });
};

export const getClaimById = (contactId: string, clientId: string, claimId: string): Promise<RawClaim> => {
  return api.base.get<RawClaim>(
    getBaseAPIRoute(BASE_API_ROUTES.contacts[':contactId'].clients[':clientId'].claims[':claimId'], {
      clientId,
      contactId,
      claimId,
    }),
  );
};

export const getPaymentMethods = async (contactId: string, clientId: string): Promise<PaymentMethod[]> => {
  return api.base
    .get<PaymentMethod[]>(
      getBaseAPIRoute(BASE_API_ROUTES.contacts[':contactId'].clients[':clientId']['payment-methods'].all, {
        contactId,
        clientId,
      }),
    )
    .then(methods => {
      if (methods.length === 0) {
        captureException(new Error(`No payment methods found for client ${clientId}`));
        throw new Error(`No payment methods found for client ${clientId}`);
      }
      return methods;
    });
};

export const updatePaymentMethod = async (
  contactId: string,
  clientId: string,
  stripePaymentMethodID: string,
): Promise<PaymentMethod> => {
  return api.base.put<PaymentMethod>(
    getBaseAPIRoute(BASE_API_ROUTES.contacts[':contactId'].clients[':clientId']['payment-methods'].all, {
      contactId,
      clientId,
    }),
    {
      stripePaymentMethodID,
    },
  );
};

type NoBillingInfoResponse = {
  billingInfoSet: false;
};

const isNoBillingInfoResponse = (response: unknown): response is NoBillingInfoResponse => {
  if (isObject(response) && 'billingInfoSet' in response) {
    return true;
  }

  return false;
};


export const getPayments = (
  contactId: string,
  clientId: string,
  page: number,
  nextToken: string,
): Promise<PaymentResponse | undefined> => {
  const params: Record<string, unknown> = {
    perPage: MAX_PAYMENT_PAGE_SIZE,
    page,
  };
  if (nextToken) {
    params.pageToken = nextToken;
  }
  return api.base
    .get<PaymentResponse | NoBillingInfoResponse>(
      getBaseAPIRoute(BASE_API_ROUTES.contacts[':contactId'].clients[':clientId'].payments.all, {
        contactId,
        clientId,
      }),

      { params },
    )
    .then(response => {
      if (!isNoBillingInfoResponse(response)) {
        return response;
      }
      return undefined;
    });
};

export const getPaymentById = (
  contactId: string,
  clientId: string,
  paymentId: string,
): Promise<Payment | undefined> => {
  return api.base
    .get<Payment | NoBillingInfoResponse>(
      getBaseAPIRoute(BASE_API_ROUTES.contacts[':contactId'].clients[':clientId'].payments[':paymentId'], {
        contactId,
        clientId,
        paymentId,
      }),
    )
    .then(response => {
      if (!isNoBillingInfoResponse(response)) {
        return response;
      }
      return undefined;
    });
};

type StripeRefundDataResponse = {
  receipt_url: string;
};

export const getStripeRefundData = (
  contactId: string,
  clientId: string,
  chargeId: string,
): Promise<StripeRefundDataResponse> => {
  return api.base.get<StripeRefundDataResponse>(
    getBaseAPIRoute(BASE_API_ROUTES.contacts[':contactId'].clients[':clientId'].charges[':chargeId'], {
      contactId,
      clientId,
      chargeId,
    }),
  );
};

export type AppointmentPriceResponse = {
  value: number;
  type: 'session' | 'evaluation';
  tier?: string;
};

export const getAppointmentPrice = (
  type: 'session' | 'evaluation',
  tier?: string,
): Promise<AppointmentPriceResponse> => {
  return api.proxy.get<AppointmentPriceResponse>(
    getBaseAPIRoute(BASE_ANONYMOUS_API_ROUTES.billing.appointment[':type'].price, {
      type,
    }),
    { params: { tier } },
  );
};

export type StripeAccountIdByStateResponse = {
  state: string;
  accountId: string;
  entity: string;
};

export const getStripeAccountIdByState = (state: string): Promise<StripeAccountIdByStateResponse> => {
  return api.proxy.get<StripeAccountIdByStateResponse>(
    getBaseAPIRoute(BASE_ANONYMOUS_API_ROUTES.billing.stripe.account[':state'], {
      state: toStateFull(state as StateAbbrev).toLowerCase(),
    }),
  );
};
